/**
 * First-touch marketing attribution capture (UTM + ad click IDs).
 *
 * Reads URL query params (primary) or the cross-subdomain of_attribution cookie
 * (backup / Stripe path), stores first-touch in localStorage with a 30-day window.
 */

export interface MarketingAttribution {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  gclid?: string
  fbclid?: string
}

interface StoredAttribution {
  data: MarketingAttribution
  timestamp: number
}

const STORAGE_KEY = 'of_attribution'
const COOKIE_KEY = 'of_attribution'
const ATTRIBUTION_WINDOW_DAYS = 30
const MAX_FIELD_LENGTH = 255

const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
] as const

type AttributionKey = (typeof ATTRIBUTION_KEYS)[number]

function normalizeValue(value: string | null | undefined): string | undefined {
  if (value == null) return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  return trimmed.slice(0, MAX_FIELD_LENGTH)
}

function pickAttribution(source: URLSearchParams | Record<string, unknown>): MarketingAttribution {
  const result: MarketingAttribution = {}
  for (const key of ATTRIBUTION_KEYS) {
    const raw =
      source instanceof URLSearchParams
        ? source.get(key)
        : (source[key] as string | undefined)
    const normalized = normalizeValue(raw ?? undefined)
    if (normalized) {
      result[key] = normalized
    }
  }
  return result
}

function hasAnyAttribution(data: MarketingAttribution): boolean {
  return ATTRIBUTION_KEYS.some((key) => Boolean(data[key]))
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const prefix = `${name}=`
  const parts = document.cookie.split(';')
  for (const part of parts) {
    const trimmed = part.trim()
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length))
    }
  }
  return null
}

function readCookieAttribution(): MarketingAttribution {
  try {
    const raw = readCookie(COOKIE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, unknown>
    return pickAttribution(parsed)
  } catch {
    return {}
  }
}

function readStored(): StoredAttribution | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredAttribution
    if (!parsed?.data || typeof parsed.timestamp !== 'number') {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    const ageDays = (Date.now() - parsed.timestamp) / (1000 * 60 * 60 * 24)
    if (ageDays > ATTRIBUTION_WINDOW_DAYS) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    localStorage.removeItem(STORAGE_KEY)
    return null
  }
}

function writeStored(data: MarketingAttribution): void {
  try {
    const payload: StoredAttribution = {
      data,
      timestamp: Date.now(),
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch (error) {
    console.error('Failed to store attribution data:', error)
  }
}

export class AttributionTrackingService {
  /**
   * Capture attribution from the current URL or cookie (first-touch only).
   * Call synchronously before React render so params are not lost on redirects.
   */
  static captureFromEnvironment(): void {
    if (typeof window === 'undefined') return

    const existing = readStored()
    if (existing) return

    const fromQuery = pickAttribution(new URLSearchParams(window.location.search))
    const fromCookie = hasAnyAttribution(fromQuery) ? {} : readCookieAttribution()
    const merged: MarketingAttribution = { ...fromCookie, ...fromQuery }

    if (hasAnyAttribution(merged)) {
      writeStored(merged)
    }
  }

  static getAttribution(): MarketingAttribution | null {
    const stored = readStored()
    if (!stored || !hasAnyAttribution(stored.data)) {
      return null
    }
    return stored.data
  }

  static clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      console.error('Failed to clear attribution data:', error)
    }
  }
}
